import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { amenitiesAPI, financialAPI, condoMembersAPI, sectionsAPI } from '../services/api';
import api from '../services/api';
import {
  Waves, Calendar, Clock, Users, CheckCircle, XCircle, Search, ChevronRight,
  AlertTriangle, Loader2, QrCode, ArrowLeft, Info, Plus, Pencil, Tag, X
} from 'lucide-react';

// ---------- Types ----------
interface Amenity {
  id: string;
  name: string;
  description: string;
  capacity: number;
  openingHours?: string;
  opening_hours?: string;
  pricePerHour?: number;
  price_per_hour?: number;
  cost_type?: string;
  cost_amount?: number;
  status: string;
  image?: string;
  features?: string[];
  requires_no_delinquency?: boolean;
  rules?: string;
  type?: string;
  schedule?: any;
  section_ids?: string[];
}

interface Booking {
  id: string;
  amenityName: string;
  amenity_name?: string;
  amenityId: string;
  amenity_id?: string;
  date: string;
  booking_date?: string;
  startTime: string;
  start_time?: string;
  endTime: string;
  end_time?: string;
  status: string;
  totalPrice: number;
  total_price?: number;
  guests: number;
}

// ---------- Helpers ----------
function mapAmenity(row: any): Amenity {
  return {
    id: String(row.id ?? ''),
    name: row.name ?? '',
    description: row.description ?? '',
    capacity: row.capacity ?? 0,
    openingHours: row.opening_hours ?? row.openingHours ?? '',
    pricePerHour: row.price_per_hour ?? row.pricePerHour ?? row.cost_amount ?? 0,
    cost_type: row.cost_type ?? 'free',
    cost_amount: row.cost_amount ?? 0,
    status: row.status ?? 'available',
    features: Array.isArray(row.features) ? row.features : [],
    requires_no_delinquency: row.requires_no_delinquency ?? true,
    section_ids: Array.isArray(row.section_ids) ? row.section_ids.map(String) : [],
  };
}

function mapBooking(row: any): Booking {
  return {
    id: String(row.id ?? ''),
    amenityName: row.amenity_name ?? row.amenityName ?? '',
    amenityId: String(row.amenity_id ?? row.amenityId ?? ''),
    date: row.booking_date ?? row.date ?? '',
    startTime: row.start_time ?? row.startTime ?? '',
    endTime: row.end_time ?? row.endTime ?? '',
    status: row.status ?? 'confirmed',
    totalPrice: row.total_price ?? row.totalPrice ?? row.cost ?? 0,
    guests: row.guests ?? row.guest_count ?? 0,
  };
}

// ---------- Component ----------
export default function Amenities() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const condoId = user?.condoId ?? '';
  const canManage = user && (user.role === 'admin' || user.role === 'super_admin');

  const [activeTab, setActiveTab] = useState<'browse' | 'my-bookings'>('browse');
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [bookingForm, setBookingForm] = useState({ date: '', startTime: '', endTime: '', guests: 1, notes: '' });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Availability / conflicts
  const [availability, setAvailability] = useState<any[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Moroso check
  const [isMoroso, setIsMoroso] = useState(false);
  const [morosoChecked, setMorosoChecked] = useState(false);

  // Success / confirmation
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<any>(null);

  // F44: Create/Edit amenity modal state
  const [showAmenityModal, setShowAmenityModal] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [amenityForm, setAmenityForm] = useState({
    name: '', description: '', capacity: 0, opening_hours: '', rules: '',
    cost_type: 'free', cost_amount: 0, type: 'general',
    requires_no_delinquency: true,
  });
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [amenitySaving, setAmenitySaving] = useState(false);
  const [amenityError, setAmenityError] = useState<string | null>(null);

  // F45: Pricing modal state
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingAmenity, setPricingAmenity] = useState<Amenity | null>(null);
  const [pricingForm, setPricingForm] = useState({
    cost_type: 'free', cost_amount: 0,
  });
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // ---------- Data loading ----------
  const loadAmenities = useCallback(async () => {
    if (!condoId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await amenitiesAPI.getAll(condoId);
      try { const sr = await sectionsAPI.getAll(condoId); const sd = sr.data?.data?.sections ?? sr.data?.sections ?? []; setSections(Array.isArray(sd) ? sd : []); } catch { setSections([]); }
      const data = res.data?.data ?? res.data ?? [];
      setAmenities(Array.isArray(data) ? data.map(mapAmenity) : []);
    } catch (err) {
      console.error('Error loading amenities:', err);
      setError('No se pudieron cargar las amenidades');
      setAmenities([]);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  const loadBookings = useCallback(async () => {
    if (!condoId) return;
    setBookingsLoading(true);
    try {
      const res = await amenitiesAPI.getBookings(condoId);
      const data = res.data?.data ?? res.data ?? [];
      const all = Array.isArray(data) ? data.map(mapBooking) : [];
      // Filter to user's bookings
      setBookings(all);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  }, [condoId]);

  const checkMoroso = useCallback(async () => {
    if (!condoId || morosoChecked) return;
    try {
      const res = await financialAPI.getMorosos(condoId);
      const morososData = res.data?.data ?? res.data ?? [];
      if (Array.isArray(morososData)) {
        const found = morososData.some((m: any) =>
          m.user_id === user?.id || m.unit === user?.unit || m.unitLabel === user?.unitLabel
        );
        setIsMoroso(found);
      }
    } catch {
      // Non-critical — allow booking
    }
    setMorosoChecked(true);
  }, [condoId, user?.id, user?.unit, user?.unitLabel, morosoChecked]);

  useEffect(() => {
    loadAmenities();
    loadBookings();
    checkMoroso();
  }, [loadAmenities, loadBookings, checkMoroso]);

  // ---------- Availability ----------
  const loadAvailability = async (amenityId: string, date: string) => {
    if (!amenityId || !date) { setAvailability([]); return; }
    setAvailabilityLoading(true);
    try {
      const res = await amenitiesAPI.getAvailability(amenityId, date);
      const data = res.data?.data ?? res.data ?? [];
      setAvailability(Array.isArray(data) ? data : []);
    } catch {
      setAvailability([]);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const isSlotConflict = (startTime: string, endTime: string): boolean => {
    if (!availability.length) return false;
    return availability.some((slot: any) => {
      const sStart = slot.start_time ?? slot.startTime ?? '';
      const sEnd = slot.end_time ?? slot.endTime ?? '';
      // Overlap check
      return (startTime < sEnd && endTime > sStart);
    });
  };

  // ---------- Booking ----------
  const openBookingModal = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setBookingForm({ date: '', startTime: '', endTime: '', guests: 1, notes: '' });
    setBookingError(null);
    setAvailability([]);
    setShowBookingModal(true);
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAmenity) return;
    setBookingError(null);

    // Validations
    if (!bookingForm.date || !bookingForm.startTime || !bookingForm.endTime) {
      setBookingError('Completa fecha y hora');
      return;
    }
    if (bookingForm.endTime <= bookingForm.startTime) {
      setBookingError('La hora de fin debe ser posterior a la de inicio');
      return;
    }
    if (selectedAmenity.capacity > 0 && bookingForm.guests > selectedAmenity.capacity) {
      setBookingError(`Excede la capacidad máxima (${selectedAmenity.capacity} personas)`);
      return;
    }
    // Past date check
    const today = new Date().toISOString().split('T')[0];
    if (bookingForm.date < today) {
      setBookingError('No puedes reservar en una fecha pasada');
      return;
    }
    // Conflict check
    if (isSlotConflict(bookingForm.startTime, bookingForm.endTime)) {
      setBookingError('El horario seleccionado ya está reservado');
      return;
    }
    // Moroso check
    if (selectedAmenity.requires_no_delinquency && isMoroso) {
      setBookingError('Tienes cargos pendientes. No puedes reservar hasta saldar tu deuda.');
      return;
    }

    setBookingSubmitting(true);
    try {
      const cost = calculateCost(selectedAmenity, bookingForm);
      const res = await amenitiesAPI.book({
        amenityName: selectedAmenity.name,
        bookingDate: bookingForm.date,
        startTime: bookingForm.startTime,
        endTime: bookingForm.endTime,
        condoId,
        unit: user?.unit ?? '',
      });

      const result = res.data?.data ?? res.data;

      // If cost > 0, route to payment
      if (cost > 0) {
        navigate('/reserva-pago', {
          state: {
            amenityName: selectedAmenity.name,
            date: bookingForm.date,
            startTime: bookingForm.startTime,
            endTime: bookingForm.endTime,
            cost,
            bookingId: result?.id,
          },
        });
        setShowBookingModal(false);
        return;
      }

      // Free reservation — show confirmation with QR
      setConfirmationData({
        amenityName: selectedAmenity.name,
        date: bookingForm.date,
        startTime: bookingForm.startTime,
        endTime: bookingForm.endTime,
        cost: 0,
        status: result?.status ?? 'confirmed',
        bookingId: result?.id,
      });
      setShowConfirmation(true);
      setShowBookingModal(false);
      await loadBookings();
    } catch (err: any) {
      console.error('Booking error:', err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al crear la reserva. Inténtalo de nuevo.';
      setBookingError(msg);
    } finally {
      setBookingSubmitting(false);
    }
  };

  const calculateCost = (amenity: Amenity, form: typeof bookingForm): number => {
    const costType = amenity.cost_type ?? 'free';
    if (costType === 'free') return 0;
    const amount = amenity.pricePerHour ?? amenity.cost_amount ?? 0;
    if (costType === 'per_hour') {
      const hours = form.endTime && form.startTime
        ? (Number(form.endTime.split(':')[0]) + Number(form.endTime.split(':')[1]) / 60) -
          (Number(form.startTime.split(':')[0]) + Number(form.startTime.split(':')[1]) / 60)
        : 0;
      return Math.max(0, amount * hours);
    }
    if (costType === 'per_event') return amount;
    return 0;
  };

  // ---------- F44: Amenity Create/Edit ----------
  const openCreateAmenity = () => {
    setEditingAmenity(null);
    setSectionIds([]);
    setAmenityForm({
      name: '', description: '', capacity: 0, opening_hours: '09:00 - 21:00',
      rules: '', cost_type: 'free', cost_amount: 0, type: 'general',
      requires_no_delinquency: true,
    });
    setAmenityError(null);
    setShowAmenityModal(true);
  };

  const openEditAmenity = (amenity: Amenity) => {
    setEditingAmenity(amenity);
    setSectionIds(Array.isArray(amenity.section_ids) ? amenity.section_ids : []);
    setAmenityForm({
      name: amenity.name || '',
      description: amenity.description || '',
      capacity: amenity.capacity || 0,
      opening_hours: amenity.openingHours || amenity.opening_hours || '09:00 - 21:00',
      rules: amenity.rules || '',
      cost_type: amenity.cost_type || 'free',
      cost_amount: amenity.pricePerHour ?? amenity.cost_amount ?? 0,
      type: amenity.type || 'general',
      requires_no_delinquency: amenity.requires_no_delinquency ?? true,
    });
    setAmenityError(null);
    setShowAmenityModal(true);
  };

  const handleAmenitySave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!condoId) { setAmenityError('Sin condominio asignado'); return; }
    if (!amenityForm.name.trim()) { setAmenityError('El nombre es obligatorio'); return; }
    setAmenitySaving(true);
    setAmenityError(null);
    try {
      if (editingAmenity) {
        await amenitiesAPI.update(condoId, editingAmenity.id, {
          name: amenityForm.name,
          description: amenityForm.description,
          capacity: amenityForm.capacity,
          opening_hours: amenityForm.opening_hours,
          rules: amenityForm.rules,
          type: amenityForm.type,
          requires_no_delinquency: amenityForm.requires_no_delinquency,
          section_ids: sectionIds,
        });
        showToast('Amenidad actualizada');
      } else {
        await amenitiesAPI.create(condoId, {
          name: amenityForm.name,
          description: amenityForm.description,
          capacity: amenityForm.capacity,
          opening_hours: amenityForm.opening_hours,
          rules: amenityForm.rules,
          type: amenityForm.type,
          cost_type: amenityForm.cost_type,
          cost_amount: amenityForm.cost_amount,
          requires_no_delinquency: amenityForm.requires_no_delinquency,
          section_ids: sectionIds,
        });
        showToast('Amenidad creada');
      }
      setShowAmenityModal(false);
      await loadAmenities();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al guardar la amenidad';
      setAmenityError(msg);
    } finally {
      setAmenitySaving(false);
    }
  };

  // ---------- F45: Amenity Pricing ----------
  const openPricingModal = (amenity: Amenity) => {
    setPricingAmenity(amenity);
    setPricingForm({
      cost_type: amenity.cost_type || 'free',
      cost_amount: amenity.pricePerHour ?? amenity.cost_amount ?? 0,
    });
    setPricingError(null);
    setShowPricingModal(true);
  };

  const handlePricingSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!condoId || !pricingAmenity) return;
    setPricingSaving(true);
    setPricingError(null);
    try {
      await amenitiesAPI.updatePricing(condoId, pricingAmenity.id, {
        cost_type: pricingForm.cost_type,
        cost_amount: Number(pricingForm.cost_amount),
      });
      showToast('Precios actualizados');
      setShowPricingModal(false);
      await loadAmenities();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Error al actualizar precios';
      setPricingError(msg);
    } finally {
      setPricingSaving(false);
    }
  };

  // ---------- Render helpers ----------
  const formatCurrency = (amount: number, currency = 'MXN') => {
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available': return { label: 'Disponible', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
      case 'maintenance': return { label: 'Mantenimiento', class: 'bg-amber-100 text-amber-700 border-amber-200' };
      case 'closed': return { label: 'Cerrado', class: 'bg-[var(--v-surface-2)] text-gray-600 border-[var(--v-border-light)]' };
      default: return { label: status, class: 'bg-[var(--v-surface-2)] text-gray-600 border-[var(--v-border-light)]' };
    }
  };

  const getBookingStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed': return { label: 'Confirmada', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
      case 'cancelled': return { label: 'Cancelada', class: 'bg-red-100 text-red-700 border-red-200' };
      case 'completed': return { label: 'Completada', class: 'bg-[var(--v-surface-2)] text-gray-600 border-[var(--v-border-light)]' };
      case 'pending_approval': return { label: 'Pendiente aprobación', class: 'bg-amber-100 text-amber-700 border-amber-200' };
      default: return { label: status, class: 'bg-[var(--v-surface-2)] text-gray-600 border-[var(--v-border-light)]' };
    }
  };

  const filteredAmenities = amenities.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         a.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // ---------- Main Render ----------
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Amenidades</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Reserva áreas comunes y disfruta de las instalaciones</p>
        </div>
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="text-[var(--v-text-secondary)] mt-4">Cargando amenidades...</p>
        </div>
      </div>
    );
  }

  if (!condoId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Amenidades</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Reserva áreas comunes</p>
        </div>
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
          <Waves className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">Sin condominio asignado</h3>
          <p className="text-[var(--v-text-secondary)]">Contacta al administrador para continuar.</p>
        </div>
      </div>
    );
  }

  if (error && amenities.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Amenidades</h2>
        </div>
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-[var(--v-text-secondary)]">{error}</p>
          <button onClick={loadAmenities} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Amenidades</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Reserva áreas comunes y disfruta de las instalaciones</p>
        </div>
        <div className="flex items-center gap-3">
          {canManage && (
            <button
              onClick={openCreateAmenity}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-all"
            >
              <Plus className="w-4 h-4" />
              Agregar Amenidad
            </button>
          )}
          <div className="flex bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-1">
            <button
              onClick={() => setActiveTab('browse')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'browse' ? 'bg-[var(--v-info)] text-white' : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-base-bg)]'
              }`}
            >
              Explorar
            </button>
            <button
              onClick={() => setActiveTab('my-bookings')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'my-bookings' ? 'bg-[var(--v-info)] text-white' : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-base-bg)]'
              }`}
            >
              Mis Reservas ({bookings.length})
            </button>
          </div>
        </div>
      </div>

      {/* Moroso Warning */}
      {isMoroso && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Tienes cargos pendientes</p>
            <p className="text-sm text-amber-700">Algunas amenidades requieren estar al corriente. Regulariza tu situación para reservar.</p>
          </div>
        </div>
      )}

      {activeTab === 'browse' ? (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-5 h-5 text-[var(--v-text-tertiary)] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar amenidades..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-[var(--v-text-primary)]"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg text-sm text-[var(--v-text-primary)]"
            >
              <option value="all">Todos los estados</option>
              <option value="available">Disponible</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="closed">Cerrado</option>
            </select>
          </div>

          {/* Amenities Grid */}
          {filteredAmenities.length === 0 ? (
            <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
              <Waves className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">No hay amenidades</h3>
              <p className="text-[var(--v-text-secondary)]">No se encontraron amenidades para tu condominio.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredAmenities.map((amenity) => {
                const status = getStatusBadge(amenity.status);
                const costAmount = amenity.pricePerHour ?? amenity.cost_amount ?? 0;
                const costLabel = amenity.cost_type === 'per_hour' ? `${formatCurrency(costAmount)}/hora`
                  : amenity.cost_type === 'per_event' ? formatCurrency(costAmount)
                  : 'Gratuito';
                return (
                  <div key={amenity.id} className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm hover:shadow-md transition-all overflow-hidden">
                    <div className="h-40 bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center relative">
                      <Waves className="w-16 h-16 text-blue-300" />
                      <div className="absolute top-4 right-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${status.class}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 bg-[var(--v-surface-1)]/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm">
                        <span className="text-sm font-semibold text-[var(--v-text-primary)]">{costLabel}</span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-1">{amenity.name}</h3>
                      <p className="text-[var(--v-text-secondary)] text-sm mb-4 line-clamp-2">{amenity.description}</p>
                      <div className="flex items-center gap-4 text-sm text-[var(--v-text-secondary)] mb-4">
                        {amenity.capacity > 0 && (
                          <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{amenity.capacity} personas</span>
                        )}
                        {(amenity.openingHours || amenity.opening_hours) && (
                          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{amenity.openingHours || amenity.opening_hours}</span>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditAmenity(amenity); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-base-bg)] hover:bg-slate-200 dark:hover:bg-slate-600 text-[var(--v-text-primary)] rounded-lg text-xs font-medium transition-all"
                          >
                            <Pencil className="w-3.5 h-3.5" /> Editar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openPricingModal(amenity); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-base-bg)] hover:bg-slate-200 dark:hover:bg-slate-600 text-[var(--v-text-primary)] rounded-lg text-xs font-medium transition-all"
                          >
                            <Tag className="w-3.5 h-3.5" /> Precios
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => openBookingModal(amenity)}
                        disabled={amenity.status !== 'available'}
                        className="w-full py-2.5 bg-[var(--v-info)] hover:bg-blue-700 disabled:bg-slate-200 disabled:text-[var(--v-text-tertiary)] disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                      >
                        {amenity.status === 'available' ? <>Reservar <ChevronRight className="w-4 h-4" /></> : 'No Disponible'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        /* My Bookings */
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          {bookingsLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
              <p className="text-[var(--v-text-secondary)] mt-4">Cargando reservas...</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">No tienes reservas</h3>
              <p className="text-[var(--v-text-secondary)]">Explora las amenidades y haz tu primera reserva.</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--v-border-light)]">
              {bookings.map((booking) => {
                const status = getBookingStatusBadge(booking.status);
                return (
                  <div key={booking.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Waves className="w-6 h-6 text-[var(--v-info)]" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-[var(--v-text-primary)]">{booking.amenityName}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[var(--v-text-secondary)]">
                          <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{booking.date}</span>
                          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{booking.startTime} - {booking.endTime}</span>
                          {booking.guests > 0 && <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{booking.guests}</span>}
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mt-3 ${status.class}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>
                    {booking.totalPrice > 0 && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-[var(--v-text-primary)]">{formatCurrency(booking.totalPrice)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ---------- Booking Modal ---------- */}
      {showBookingModal && selectedAmenity && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Reservar {selectedAmenity.name}</h3>
                <p className="text-[var(--v-text-secondary)] text-sm mt-1">Completa los detalles</p>
              </div>
              <button onClick={() => setShowBookingModal(false)} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBooking} className="p-6 space-y-4">
              {/* Amenity Info */}
              <div className="bg-[var(--v-base-bg)] p-4 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--v-text-secondary)]">Capacidad máxima</span>
                  <span className="text-sm font-medium text-[var(--v-text-primary)]">{selectedAmenity.capacity} personas</span>
                </div>
                {(selectedAmenity.openingHours || selectedAmenity.opening_hours) && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--v-text-secondary)]">Horario</span>
                    <span className="text-sm font-medium text-[var(--v-text-primary)]">{selectedAmenity.openingHours || selectedAmenity.opening_hours}</span>
                  </div>
                )}
                {(() => {
                  const cost = calculateCost(selectedAmenity, bookingForm);
                  if (cost > 0) {
                    return (
                      <div className="flex items-center justify-between pt-2 border-t border-[var(--v-border-light)]">
                        <span className="text-sm text-[var(--v-text-secondary)]">Costo estimado</span>
                        <span className="text-lg font-bold text-[var(--v-text-primary)]">{formatCurrency(cost)}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Moroso warning inside modal */}
              {selectedAmenity.requires_no_delinquency && isMoroso && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-amber-800">Tienes cargos pendientes. El sistema no permitirá reservar esta amenidad.</span>
                </div>
              )}

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fecha</label>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={bookingForm.date}
                  onChange={(e) => {
                    setBookingForm({ ...bookingForm, date: e.target.value });
                    if (selectedAmenity && e.target.value) {
                      loadAvailability(selectedAmenity.id, e.target.value);
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  required
                />
              </div>

              {/* Time Slots */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Hora inicio</label>
                  <input
                    type="time"
                    value={bookingForm.startTime}
                    onChange={(e) => setBookingForm({ ...bookingForm, startTime: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)] ${
                      bookingForm.startTime && isSlotConflict(bookingForm.startTime, bookingForm.endTime)
                        ? 'border-red-300'
                        : 'border-[var(--v-border-light)]'
                    }`}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Hora fin</label>
                  <input
                    type="time"
                    value={bookingForm.endTime}
                    onChange={(e) => setBookingForm({ ...bookingForm, endTime: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)] ${
                      bookingForm.startTime && isSlotConflict(bookingForm.startTime, bookingForm.endTime)
                        ? 'border-red-300'
                        : 'border-[var(--v-border-light)]'
                    }`}
                    required
                  />
                </div>
              </div>

              {/* Conflict warning */}
              {bookingForm.startTime && bookingForm.endTime && isSlotConflict(bookingForm.startTime, bookingForm.endTime) && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700">Este horario ya está reservado. Elige otro.</span>
                </div>
              )}

              {/* Availability Loading */}
              {availabilityLoading && (
                <div className="flex items-center gap-2 text-sm text-[var(--v-text-secondary)]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verificando disponibilidad...
                </div>
              )}

              {/* Occupied slots */}
              {availability.length > 0 && !availabilityLoading && (
                <div className="bg-[var(--v-base-bg)] p-3 rounded-lg">
                  <p className="text-xs font-medium text-[var(--v-text-secondary)] mb-2">Horarios ocupados:</p>
                  <div className="flex flex-wrap gap-2">
                    {availability.map((slot: any, idx: number) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                        {slot.start_time ?? slot.startTime} - {slot.end_time ?? slot.endTime}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Guests */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Invitados</label>
                <input
                  type="number"
                  min="1"
                  max={selectedAmenity.capacity || 100}
                  value={bookingForm.guests}
                  onChange={(e) => setBookingForm({ ...bookingForm, guests: Number(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  required
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Notas (opcional)</label>
                <textarea
                  rows={2}
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)] resize-none"
                  placeholder="Algún requerimiento especial..."
                />
              </div>

              {bookingError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{bookingError}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  disabled={bookingSubmitting}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-base-bg)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={bookingSubmitting}
                  className="flex-1 px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {bookingSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Reservando...</>
                  ) : calculateCost(selectedAmenity, bookingForm) > 0 ? (
                    'Confirmar y Pagar'
                  ) : (
                    'Confirmar Reserva'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------- Confirmation with QR ---------- */}
      {showConfirmation && confirmationData && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--v-text-primary)] mb-2">¡Reserva confirmada!</h2>
            <p className="text-[var(--v-text-secondary)] mb-6">{confirmationData.amenityName}</p>

            <div className="bg-[var(--v-base-bg)] p-4 rounded-xl mb-6 text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-[var(--v-text-secondary)]">Fecha</span>
                <span className="text-sm font-medium text-[var(--v-text-primary)]">{confirmationData.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[var(--v-text-secondary)]">Hora</span>
                <span className="text-sm font-medium text-[var(--v-text-primary)]">{confirmationData.startTime} - {confirmationData.endTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[var(--v-text-secondary)]">Estado</span>
                <span className="text-sm font-medium text-emerald-600">{confirmationData.status === 'confirmed' ? 'Confirmada' : 'Pendiente'}</span>
              </div>
            </div>

            {/* QR Code */}
            <div className="bg-[var(--v-surface-1)] p-4 rounded-xl mb-6 inline-block">
              <div className="w-40 h-40 bg-slate-100 rounded-lg flex items-center justify-center">
                <QrCode className="w-32 h-32 text-slate-800" />
              </div>
              <p className="text-xs text-[var(--v-text-secondary)] mt-2">Muestra este código en la caseta</p>
            </div>

            <button
              onClick={() => { setShowConfirmation(false); setConfirmationData(null); }}
              className="w-full px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Aceptar
            </button>
          </div>
        </div>
      )}

      {/* ---------- F44: Create/Edit Amenity Modal ---------- */}
      {showAmenityModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">
                  {editingAmenity ? 'Editar Amenidad' : 'Nueva Amenidad'}
                </h3>
                <p className="text-[var(--v-text-secondary)] text-sm mt-1">Completa los detalles</p>
              </div>
              <button onClick={() => setShowAmenityModal(false)} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAmenitySave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Nombre *</label>
                <input
                  type="text"
                  value={amenityForm.name}
                  onChange={(e) => setAmenityForm({ ...amenityForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  placeholder="Ej: Alberca, Gimnasio, Salón..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Descripción</label>
                <textarea
                  rows={3}
                  value={amenityForm.description}
                  onChange={(e) => setAmenityForm({ ...amenityForm, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)] resize-none"
                  placeholder="Descripción de la amenidad..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Capacidad</label>
                  <input
                    type="number"
                    min="0"
                    value={amenityForm.capacity}
                    onChange={(e) => setAmenityForm({ ...amenityForm, capacity: Number(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Tipo</label>
                  <select
                    value={amenityForm.type}
                    onChange={(e) => setAmenityForm({ ...amenityForm, type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  >
                    <option value="general">General</option>
                    <option value="pool">Alberca</option>
                    <option value="gym">Gimnasio</option>
                    <option value="salon">Salón</option>
                    <option value="court">Cancha</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Horario (apertura - cierre)</label>
                <input
                  type="text"
                  value={amenityForm.opening_hours}
                  onChange={(e) => setAmenityForm({ ...amenityForm, opening_hours: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  placeholder="Ej: 09:00 - 21:00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Reglas</label>
                <textarea
                  rows={3}
                  value={amenityForm.rules}
                  onChange={(e) => setAmenityForm({ ...amenityForm, rules: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)] resize-none"
                  placeholder="Reglas de uso..."
                />
              </div>

              {!editingAmenity && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Tipo de costo</label>
                    <select
                      value={amenityForm.cost_type}
                      onChange={(e) => setAmenityForm({ ...amenityForm, cost_type: e.target.value })}
                      className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                    >
                      <option value="free">Gratis</option>
                      <option value="fixed">Costo fijo</option>
                      <option value="per_hour">Por hora</option>
                      <option value="per_event">Por evento</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Monto</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amenityForm.cost_amount}
                      onChange={(e) => setAmenityForm({ ...amenityForm, cost_amount: Number(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                      disabled={amenityForm.cost_type === 'free'}
                    />
                  </div>
                </div>
              )}

              {sections.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Secciones</label>
                  <select multiple value={sectionIds} onChange={(e) => setSectionIds(Array.from(e.target.selectedOptions, option => option.value))}
                    className="w-full min-h-24 px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg bg-[var(--v-surface-1)] text-[var(--v-text-primary)]">
                    {sections.map(section => <option key={section.id} value={section.id}>{section.name}</option>)}
                  </select>
                  <p className="text-xs text-[var(--v-text-secondary)] mt-1">Sin selección: Todo el condominio</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_no_delinquency"
                  checked={amenityForm.requires_no_delinquency}
                  onChange={(e) => setAmenityForm({ ...amenityForm, requires_no_delinquency: e.target.checked })}
                  className="w-4 h-4 rounded border-[var(--v-border-light)]"
                />
                <label htmlFor="requires_no_delinquency" className="text-sm text-[var(--v-text-primary)]">
                  Requiere estar al corriente (no moroso)
                </label>
              </div>

              {amenityError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{amenityError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAmenityModal(false)}
                  disabled={amenitySaving}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-base-bg)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={amenitySaving}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {amenitySaving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : editingAmenity ? 'Guardar Cambios' : 'Crear Amenidad'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------- F45: Pricing Modal ---------- */}
      {showPricingModal && pricingAmenity && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Precios — {pricingAmenity.name}</h3>
                <p className="text-[var(--v-text-secondary)] text-sm mt-1">Configura el costo de la amenidad</p>
              </div>
              <button onClick={() => setShowPricingModal(false)} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePricingSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Tipo de costo</label>
                <select
                  value={pricingForm.cost_type}
                  onChange={(e) => setPricingForm({ ...pricingForm, cost_type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                >
                  <option value="free">Gratis</option>
                  <option value="fixed">Costo fijo</option>
                  <option value="per_hour">Por hora</option>
                  <option value="per_event">Por evento</option>
                  <option value="dynamic">Dinámico</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Monto</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricingForm.cost_amount}
                  onChange={(e) => setPricingForm({ ...pricingForm, cost_amount: Number(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  disabled={pricingForm.cost_type === 'free'}
                />
              </div>

              {pricingError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{pricingError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPricingModal(false)}
                  disabled={pricingSaving}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-base-bg)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={pricingSaving}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {pricingSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : 'Guardar Precios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------- Toast ---------- */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          <span className="text-sm font-medium">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
